import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import { get, post } from "@rails/request.js"

/**
 * Revenue Verification Poll Controller
 *
 * Polls the server for revenue verification status after a Plaid connection
 * is made. When Plaid first connects, transaction data may not be immediately
 * available. This controller polls until verification completes or fails.
 *
 * All business logic (retry counting, timeout decisions) lives on the backend.
 * On poll exhaustion, this controller POSTs to poll-timeout and lets the server
 * respond with the appropriate Turbo Stream partial.
 */
export default class extends Controller {
  static targets = ["statusMessage", "progressText", "progressBar"]

  static values = {
    url: String,
    applicationId: String,
    pollTimeoutUrl: { type: String, default: "" },
    pollInterval: { type: Number, default: 3000 }, // Poll every 3 seconds
    maxAttempts: { type: Number, default: 40 }      // Max 2 minutes of polling
  }

  connect() {
    this.attempts = 0
    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    // Start polling immediately
    this.poll()

    // Then set up interval
    this.pollTimer = setInterval(() => {
      this.poll()
    }, this.pollIntervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async poll() {
    this.attempts++

    // Update progress text
    if (this.hasProgressTextTarget) {
      if (this.attempts < 5) {
        this.progressTextTarget.textContent = "Connecting to your bank..."
      } else if (this.attempts < 15) {
        this.progressTextTarget.textContent = "Retrieving transaction history..."
      } else if (this.attempts < 30) {
        this.progressTextTarget.textContent = "Analyzing revenue data..."
      } else {
        this.progressTextTarget.textContent = "Almost done..."
      }
    }

    // Advance progress bar based on attempts
    if (this.hasProgressBarTarget) {
      const progress = Math.min(95, 5 + (this.attempts / this.maxAttemptsValue) * 90)
      this.progressBarTarget.style.width = `${Math.round(progress)}%`
    }

    // Stop if we've exceeded max attempts
    if (this.attempts >= this.maxAttemptsValue) {
      this.stopPolling()
      this.handlePollExhausted()
      return
    }

    try {
      const response = await get(this.urlValue, { responseKind: "json" })

      if (!response.ok) {
        console.error('Poll request failed:', response.statusCode)
        return
      }

      const data = await response.json

      if (data.complete) {
        this.stopPolling()

        if (data.status === 'verified') {
          this.handleVerified()
        } else if (data.status === 'failed') {
          // Backend job already broadcast the appropriate state via Turbo Stream.
          // If the broadcast was missed, fall back to poll-timeout to get correct state.
          this.handlePollExhausted()
        }
      } else {
        // Still pending, update status message
        if (this.hasStatusMessageTarget && data.message) {
          this.statusMessageTarget.textContent = data.message
        }
      }
    } catch (error) {
      console.error('Error polling verification status:', error)
      // Continue polling on network errors
    }
  }

  handleVerified() {
    // Use Turbo.visit instead of window.location.reload() to avoid
    // triggering beforeunload beacon which can race with step changes
    this.disableAutoSave()
    Turbo.visit(window.location.href, { action: "replace" })
  }

  async handlePollExhausted() {
    const url = this.pollTimeoutUrlValue
    if (!url) {
      // No poll-timeout URL configured — fall back to page reload
      this.disableAutoSave()
      Turbo.visit(window.location.href, { action: "replace" })
      return
    }

    try {
      const response = await post(url, { responseKind: "turbo-stream" })

      if (!response.ok) {
        console.error('Poll timeout request failed:', response.statusCode)
        this.disableAutoSave()
        Turbo.visit(window.location.href, { action: "replace" })
      }
    } catch (error) {
      console.error('Error requesting poll timeout:', error)
      this.disableAutoSave()
      Turbo.visit(window.location.href, { action: "replace" })
    }
  }

  disableAutoSave() {
    // Clear auto-save state to prevent beacon firing during navigation
    const autoSaveEl = document.querySelector('[data-controller*="auto-save"]')
    if (autoSaveEl) {
      const autoSaveCtrl = this.application.getControllerForElementAndIdentifier(autoSaveEl, 'auto-save')
      if (autoSaveCtrl) {
        autoSaveCtrl.hasChanges = false
        autoSaveCtrl.isSaving = false
      }
    }
  }
}
